import { z } from 'zod';

/**
 * High-level grouping for a `type: 'hub'` region.
 *
 * - `port`             — sea / dry / oil ports and refinery jetties.
 * - `logistics_hub`    — industrial cities, free zones, warehouse districts, fulfillment centers, etc.
 * - `border_crossing`  — land / maritime crossings between countries.
 */
export const regionHubCategorySchema = z.enum(['port', 'logistics_hub', 'border_crossing']);
export type RegionHubCategory = z.infer<typeof regionHubCategorySchema>;

/**
 * Fine-grained hub sub-type, used for UX filters and routing scores.
 *
 * Values sourced from the Saudi logistics master Excel `hub_type` column.
 */
export const regionHubTypeSchema = z.enum([
  'seaport',
  'dry_port',
  'free_zone',
  'industrial_city',
  'airport_logistics',
  'cold_chain',
  'customs_zone',
  'economic_city',
  'fulfillment_center',
  'freight_terminal',
  'land_crossing',
  'logistics_zone',
  'maritime_crossing',
  'sez',
  'warehouse_district',
  'wholesale_market',
]);
export type RegionHubType = z.infer<typeof regionHubTypeSchema>;

/**
 * Hub-specific block on a Region. Present iff `type === 'hub'`.
 */
export const regionHubInfoSchema = z.object({
  /** High-level grouping (port / logistics_hub / border_crossing). */
  category: regionHubCategorySchema,
  /** Fine-grained sub-type (seaport / dry_port / free_zone / ...). */
  hubType: regionHubTypeSchema,
  /**
   * Border-crossings only — `customCode` of the connected country
   * (e.g. `'united_arab_emirates'` for the Al Ghuwaifat / Salwa crossings).
   */
  connectsToCountryCustomCode: z.string().trim().optional(),
});
export type RegionHubInfo = z.infer<typeof regionHubInfoSchema>;
